/**
 * Notification Sound Utility
 * Provides audio notifications for various events in the CRM
 */

// Sound URLs (using simple web audio tones)
const SOUNDS = {
  notification: null,
  chat: null,
  success: null,
  alert: null
};

// Audio context for generating tones
let audioContext = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

/**
 * Play a simple notification tone
 * @param {string} type - Type of notification: 'notification', 'chat', 'success', 'alert'
 * @param {number} volume - Volume from 0 to 1 (default: 0.3)
 */
export const playNotificationSound = (type = 'notification', volume = 0.3) => {
  // Check if sounds are enabled
  const soundsEnabled = localStorage.getItem('notificationSoundsEnabled') !== 'false';
  if (!soundsEnabled) return;

  try {
    const ctx = getAudioContext();
    
    // Resume audio context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Different tones for different notification types
    switch (type) {
      case 'chat':
        // Two-tone chat notification (like a message)
        oscillator.frequency.setValueAtTime(880, ctx.currentTime); // A5
        oscillator.frequency.setValueAtTime(1100, ctx.currentTime + 0.1); // C#6
        gainNode.gain.setValueAtTime(volume, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
        break;
        
      case 'success':
        // Rising tone for success
        oscillator.frequency.setValueAtTime(523, ctx.currentTime); // C5
        oscillator.frequency.setValueAtTime(659, ctx.currentTime + 0.1); // E5
        oscillator.frequency.setValueAtTime(784, ctx.currentTime + 0.2); // G5
        gainNode.gain.setValueAtTime(volume, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.4);
        break;
        
      case 'alert':
        // Attention-grabbing alert
        oscillator.frequency.setValueAtTime(440, ctx.currentTime); // A4
        oscillator.frequency.setValueAtTime(440, ctx.currentTime + 0.15);
        oscillator.frequency.setValueAtTime(440, ctx.currentTime + 0.3);
        gainNode.gain.setValueAtTime(volume, ctx.currentTime);
        gainNode.gain.setValueAtTime(0.01, ctx.currentTime + 0.1);
        gainNode.gain.setValueAtTime(volume, ctx.currentTime + 0.15);
        gainNode.gain.setValueAtTime(0.01, ctx.currentTime + 0.25);
        gainNode.gain.setValueAtTime(volume, ctx.currentTime + 0.3);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.5);
        break;
        
      case 'notification':
      default:
        // Soft notification ding
        oscillator.frequency.setValueAtTime(830, ctx.currentTime); // G#5
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(volume, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.2);
        break;
    }
  } catch (err) {
    console.debug('Could not play notification sound:', err);
  }
};

/**
 * Toggle notification sounds on/off
 * @returns {boolean} New state
 */
export const toggleNotificationSounds = () => {
  const current = localStorage.getItem('notificationSoundsEnabled') !== 'false';
  const newState = !current;
  localStorage.setItem('notificationSoundsEnabled', newState.toString());
  return newState;
};

/**
 * Check if notification sounds are enabled
 * @returns {boolean}
 */
export const areSoundsEnabled = () => {
  return localStorage.getItem('notificationSoundsEnabled') !== 'false';
};

/**
 * Set notification sounds enabled/disabled
 * @param {boolean} enabled
 */
export const setSoundsEnabled = (enabled) => {
  localStorage.setItem('notificationSoundsEnabled', enabled.toString());
};

export default {
  playNotificationSound,
  toggleNotificationSounds,
  areSoundsEnabled,
  setSoundsEnabled
};
